import type { OpportunitiesResponse, SingleOpportunity } from '@ps/isp-web-services-types';

import { webServicesCurrencyToCoreCurrencyMap } from '@swf/sports/utils/webServicesCurrencyMap';

import { initialState } from '..';
import type { BetSlipState } from '../types';
import { generateAcceptedOdds } from '../utils/generateAcceptedOdds';

const betSlipUpdated = (state: BetSlipState, payload: OpportunitiesResponse): BetSlipState => {
  let { selectedPromotionPicker } = state;
  const isSinglePromotionPicker = payload.opportunities.SINGLE.some(
    (singleOpportunity) => selectedPromotionPicker === singleOpportunity.id
  );
  const isMultiplePromotionPicker = payload.opportunities.MULTIPLE.some(
    (multipleOpportunity) => selectedPromotionPicker === multipleOpportunity.id
  );

  // Reset the promotion picker state if a multiple opportunity is no longer returned
  if (!isSinglePromotionPicker && !isMultiplePromotionPicker) {
    ({ selectedPromotionPicker } = initialState);
  }

  const updatedSingles = payload.opportunities.SINGLE.map((opportunity) => {
    // Patch suspended or unavailable opportunities with previous data
    if (!['UNAVAILABLE', 'SUSPENDED'].includes(opportunity.status)) {
      return opportunity;
    }

    // Reset the promotion picker state if that opportunity becomes UNAVAILABLE or SUSPENDED
    if (selectedPromotionPicker === opportunity.id) {
      ({ selectedPromotionPicker } = initialState);
    }

    // There is enough information to display an opportunity
    if (opportunity.name && opportunity.description) return opportunity;

    const matchedOpp = state.opportunities.SINGLE.find(
      (stateOpportunity) => stateOpportunity.id === opportunity.id
    );
    // If there is no previous opportunity in the state and the opportunity is no longer available
    // This edge case happens when you select selection but the opportunity is closed
    if (!matchedOpp) return opportunity;

    return {
      ...matchedOpp,
      status: opportunity.status,
      marketStatus: opportunity.marketStatus,
      runnerId: opportunity.runnerId,
      failureCode: 'failureCode' in opportunity ? opportunity.failureCode : undefined
    };
  }).filter((opp): opp is SingleOpportunity => !!opp);

  const allOpportunities = [...updatedSingles, ...payload.opportunities.MULTIPLE];
  const opportunitiesIds = allOpportunities.map(({ id }) => id);

  // Add new accepted odds
  const acceptedOdds = generateAcceptedOdds(state.acceptedOdds, allOpportunities);

  // Remove accepted odds if the opportunity is no longer in the betslip
  Object.keys(acceptedOdds).forEach((id) => {
    if (!opportunitiesIds.includes(id)) delete acceptedOdds[id];
  });

  return {
    ...state,
    acceptedOdds,
    opportunities: {
      SINGLE: updatedSingles,
      MULTIPLE: payload.opportunities.MULTIPLE
    },
    wallets: payload.wallets,
    selectedPromotionPicker,
    hasInvalidCombination: payload.hasInvalidCombination,
    requiredPlacementCurrency: payload.requiredPlacementCurrency
      ? webServicesCurrencyToCoreCurrencyMap[payload.requiredPlacementCurrency]
      : undefined,
    dailyMaxPayoutLimit: payload.dailyMaxPayoutLimit,
    fetchStatus: 'READY',
    /**
     * Temporary until GSFET-1654 is actioned
     */
    errorCode:
      payload.errorCode === 'PLAY_MONEY_NOT_SUPPORTED'
        ? payload.errorCode
        : state.errorCode === 'PLAY_MONEY_NOT_SUPPORTED' && !payload.errorCode
          ? undefined
          : state.errorCode
  };
};

export default betSlipUpdated;
